import { useEffect, useRef, useState } from "react";
import { usePopupController } from "./usePopupController";

const HERO_PX = 600; // “top of page” band; tweak if needed

function playingHeroVideo() {
  try {
    const v = document.querySelector("video");
    if (!v) return false;
    const r = v.getBoundingClientRect();
    const visible = r.top < window.innerHeight && r.bottom > 0;
    return visible && !v.paused && !v.ended;
  } catch {
    return false;
  }
}

function eligibleNow() {
  if (typeof window === "undefined") return false;
  const atTop = window.scrollY <= HERO_PX;
  return !atTop && !playingHeroVideo();
}

export function useTimerLeadPopup({ delaySeconds = 60 } = {}) {
  const { requestShow } = usePopupController();
  const [show, setShow] = useState(false);
  const startedRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => {
    // If already eligible, start immediately; otherwise wait for first eligibility
    function tryStart() {
      if (startedRef.current) return;
      if (eligibleNow()) {
        startedRef.current = true;

        // 🔊 DEV-ONLY: log when the countdown actually starts
        // console.log(`[DEV] TimerLeadPopup: started countdown (${delaySeconds}s) at ${new Date().toISOString()}`);

        // start countdown now that user has left hero/video
        timerRef.current = setTimeout(() => {
          requestShow("timerLeadPopup", true);
          if (!show) setShow(true);
        }, delaySeconds * 1000);
        // once started, we can drop listeners
        detach();
      }
    }

    function onScroll() { tryStart(); }
    function onVideoEvent() { tryStart(); }

    function attach() {
      window.addEventListener("scroll", onScroll, { passive: true });
      // listen for video ending/pausing
      const v = document.querySelector("video");
      v?.addEventListener("ended", onVideoEvent);
      v?.addEventListener("pause", onVideoEvent);
    }
    function detach() {
      window.removeEventListener("scroll", onScroll);
      const v = document.querySelector("video");
      v?.removeEventListener("ended", onVideoEvent);
      v?.removeEventListener("pause", onVideoEvent);
    }

    // kick it off
    tryStart();
    if (!startedRef.current) attach();

    return () => {
      detach();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delaySeconds, requestShow]);

  return { show, setShow };
}
